import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { FilterDefinition } from '../data-table/column-definition.model';

@Component({
  selector: 'cosmos-filter-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, ButtonModule, DatePickerModule, InputTextModule, PanelModule, SelectModule, TooltipModule],
  templateUrl: 'filter-panel.component.html',
})
export class FilterPanelComponent {
  private readonly translateService = inject(TranslateService);

  readonly filters = input.required<FilterDefinition[]>();

  readonly search = output<Record<string, any>>();
  readonly reset = output<void>();

  protected collapsed = false;
  protected values: Record<string, any> = {};
  protected resolvedOptions: Record<string, { label: string; value: any }[]> = {};

  constructor() {
    effect(() => {
      // Both methods read filters() — must run inside effect, not constructor,
      // because input.required() is not available until after construction.
      this.initValues();
      this.resolveOptions();
    });
  }

  private resolveOptions(): void {
    for (const f of this.filters()) {
      if (f.type === 'select') {
        if (f.translatePrefix && f.values) {
          this.resolvedOptions[f.name] = f.values.map(v => ({
            label: this.translateService.instant(`${f.translatePrefix}${v}`),
            value: v,
          }));
        } else {
          this.resolvedOptions[f.name] = f.options ?? [];
        }
      }
    }
  }

  private initValues(): void {
    this.values = {};
    for (const f of this.filters()) {
      this.values[f.name] = null;
    }
  }

  onSearch(): void {
    const active: Record<string, any> = {};
    for (const f of this.filters()) {
      const val = this.values[f.name];
      if (val === null || val === undefined || val === '') continue;
      if (f.type === 'dateRange' && Array.isArray(val)) {
        const [from, to] = val as (Date | null)[];
        if (from) active[`${f.name}From`] = from.toISOString();
        if (to) active[`${f.name}To`] = to.toISOString();
      } else {
        active[f.name] = val;
      }
    }
    this.search.emit(active);
  }

  onReset(): void {
    this.initValues();
    this.reset.emit();
  }

  hasActiveFilters(): boolean {
    return Object.values(this.values).some(v => this.isValueActive(v));
  }

  activeFilterCount(): number {
    return Object.values(this.values).filter(v => this.isValueActive(v)).length;
  }

  private isValueActive(v: any): boolean {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.some(item => item !== null && item !== undefined);
    return true;
  }
}
